// Dedicated, conversion-focused SEO landing pages targeting high-impression
// Google Search Console queries (storm/wind damage, local roofing, maintenance).
const BASE = "https://fabbuildpro.com";

const STORM_IMG =
  "https://customer-assets.emergentagent.com/job_fabb-zip-search/artifacts/fc6e53pk_How-Storm-Damage-Shows-Up-on-Your-Roof.webp";
const MAINT_IMG =
  "https://customer-assets.emergentagent.com/job_fabb-zip-search/artifacts/o5poyoyv_Top-Roof-Maintenance-Tips-You-Need-To-Know--rgx0v5bd8fcssx2rxgdpec6vz53hpll41q25w2jcds.webp";

export const landingPages = {
  "storm-damage": {
    path: "/storm-damage-roof-repair-virginia",
    serviceType: "Storm Damage Roof Repair",
    eyebrow: "24/7 Storm Response · Insurance Specialists",
    urgency: "24/7 Emergency Response · Same-Day Inspections",
    h1: "Fast Storm Damage Roof Repair in Northern Virginia",
    metaTitle: "Storm Damage Roof Repair Northern Virginia | Fab Build Pro",
    metaDesc:
      "Emergency storm & hail damage roof repair across Northern Virginia & the DMV. Insurance claim specialists. Free inspections — call 703-766-6599.",
    heroImage: STORM_IMG,
    heroAlt: "Storm damage on a Northern Virginia roof repaired by Fab Build Pro",
    ctaHeading: "Schedule Your Free Roof Inspection",
    intro: [
      "When severe weather hits the DMV, Fab Build Pro is the storm-damage roof repair team Northern Virginia homeowners trust. High winds, hail, and debris can compromise even a newer roof — and the damage often isn't visible from the ground. Our GAF Master Elite crews respond fast, document everything the way insurance adjusters expect, and restore your roof to code with premium materials.",
      "We offer 24/7 storm response and can typically inspect and secure your roof within 24–48 hours. Best of all, many homeowners qualify to repair or fully replace their roof for little more than their insurance deductible. Call 703-766-6599 for a free, no-obligation storm inspection.",
    ],
    sections: [
      {
        h2: "Emergency Roof Inspections After Storms",
        body: [
          "After a storm, the smartest first step is a professional inspection. We climb the roof, document every impact point with photos, and give you an honest, written assessment — free of charge. If your roof is actively leaking, we provide emergency tarping to protect your home while repairs are scheduled.",
        ],
      },
      {
        h2: "Wind Damage Roof Repair",
        body: [
          "Sustained winds and gusts can lift, crease, and tear away shingles, exposing the underlayment and decking to water. We repair wind-damaged shingles, flashing, ridge caps, and vents — and check the surrounding areas for hidden damage that leads to future leaks.",
        ],
      },
      {
        h2: "Hail Damage Repair",
        body: [
          "Hail leaves bruises and knocks protective granules loose, shortening your roof's lifespan even when it looks fine from the driveway. We identify hail impacts adjusters look for and make sure they're properly documented for your claim.",
        ],
      },
      {
        h2: "Missing Shingles & Roof Leak Repair",
        body: [
          "Missing shingles and post-storm leaks are the most common calls we get. We match your existing shingles where possible, seal vulnerable areas, and trace leaks to their true source — not just the spot where water shows up inside.",
        ],
      },
      {
        h2: "Insurance Claim Assistance — Start to Finish",
        body: [
          "Filing a roof damage claim can be overwhelming. We handle the entire process: inspection, documentation, meeting your adjuster on-site, and completing the approved repairs to code. Our goal is to make your claim smooth and maximize your covered restoration.",
        ],
      },
      {
        h2: "How the Process Works",
        body: [
          "1) Call us for a free storm inspection. 2) We document all damage with photos and a written report. 3) We help you file your insurance claim and meet the adjuster. 4) Once approved, our crews complete the repair or replacement — usually in 1–2 days — and finish with a full clean-up and magnetic nail sweep.",
        ],
      },
    ],
    faqs: [
      { q: "How do I know if my roof has storm or wind damage?", a: "Look for lifted, creased, or missing shingles, granules in your gutters, dented flashing or vents, and interior water stains. Much of this damage is hard to see from the ground, which is why we offer a free professional inspection." },
      { q: "Does insurance cover storm damage roof repairs?", a: "In most cases, yes — if the damage is from a covered event like wind or hail. We document everything the way adjusters expect and manage the claim from start to finish, so many homeowners pay little more than their deductible." },
      { q: "How fast can you respond after a storm?", a: "We offer 24/7 storm response across Northern Virginia and the DMV and can typically inspect and secure (tarp) your roof within 24–48 hours." },
      { q: "How much does storm damage roof repair cost in Northern Virginia?", a: "It varies with the extent of damage. Minor repairs often run $300–$1,500, while insurance-covered replacements are frequently completed for the cost of your deductible. Your free inspection includes a written estimate." },
    ],
    related: [
      { label: "Wind Damage Roof Repair", to: "/wind-damage-roof-repair" },
      { label: "Roof Replacement", to: "/services/roof-replacement" },
      { label: "Roofing Contractor McLean VA", to: "/roofing-contractor-mclean-va" },
      { label: "Roof Maintenance", to: "/roof-maintenance" },
    ],
  },

  "wind-damage": {
    path: "/wind-damage-roof-repair",
    serviceType: "Wind Damage Roof Repair",
    eyebrow: "Wind Damage Specialists · 24/7 Response",
    urgency: "Same-Day Service Available · 24/7 Emergency",
    h1: "Fast Wind Damage Roof Repair in Northern Virginia",
    metaTitle: "Wind Damage Roof Repair Northern Virginia | Fab Build Pro",
    metaDesc:
      "Wind-damaged roof? Fab Build Pro repairs wind & storm roof damage across Northern Virginia. Insurance help & 24/7 emergency service. Call 703-766-6599.",
    heroImage: STORM_IMG,
    heroAlt: "Wind-damaged roof shingles being repaired by Fab Build Pro in Virginia",
    ctaHeading: "Schedule Your Free Roof Inspection",
    intro: [
      "Wind is one of the most common — and most underestimated — causes of roof damage in Northern Virginia. Fab Build Pro specializes in wind damage roof repair, from a few lifted shingles to major storm restoration. We find the damage, document it for your insurance, and repair it right the first time.",
      "If you've had a storm roll through and suspect your roof took a hit, call 703-766-6599 for a free wind damage inspection. We respond 24/7 for emergencies.",
    ],
    sections: [
      {
        h2: "How Wind Speeds Damage Your Roof",
        body: [
          "Roof shingles are rated to withstand specific wind speeds, but repeated gusts weaken the seal strips over time. Winds of 45–57 mph can start lifting older or poorly installed shingles, while gusts above 58 mph (severe thunderstorm territory in the DMV) can tear shingles off completely and drive rain under the roof covering.",
        ],
      },
      {
        h2: "Signs of Wind Damage",
        bullets: [
          "Lifted, curled, or creased shingles",
          "Missing shingles or exposed underlayment",
          "Granules collecting in gutters and downspouts",
          "Loose or damaged flashing around chimneys and vents",
          "Debris on the roof and in the yard",
          "New water stains on ceilings or in the attic",
        ],
        body: [
          "Even if you only spot one or two of these signs, there may be more hidden damage. A free professional inspection is the safest way to know for sure.",
        ],
      },
      {
        h2: "Insurance Documentation Done Right",
        body: [
          "Wind damage claims live or die on documentation. We photograph every affected area, note dates and conditions, and present the evidence in the format adjusters expect. We'll even meet your adjuster on-site to advocate for a fair, complete restoration.",
        ],
      },
      {
        h2: "Emergency Wind Damage Repair",
        body: [
          "Exposed decking and torn shingles let water in fast. Our team provides emergency tarping and rapid repairs to protect your home, then completes permanent repairs with premium, wind-rated materials backed by our workmanship guarantee.",
        ],
      },
    ],
    faqs: [
      { q: "How do I know if my roof has wind damage?", a: "Common signs include lifted or missing shingles, creased shingles, granules in the gutters, damaged flashing, and interior water stains. Because much wind damage is subtle, we recommend a free professional inspection after any major windstorm." },
      { q: "What wind speed causes roof damage?", a: "Shingles can begin lifting at sustained winds of 45–57 mph, and gusts above 58 mph frequently tear shingles off entirely. Older or improperly installed roofs are vulnerable at lower speeds." },
      { q: "Will insurance pay for wind damage roof repair?", a: "Wind is a covered peril on most homeowner policies. We document the damage thoroughly and manage your claim end to end, so many homeowners restore their roof for close to their deductible." },
    ],
    related: [
      { label: "Storm Damage Roof Repair", to: "/storm-damage-roof-repair-virginia" },
      { label: "Roof Repair", to: "/services/roof-repair" },
      { label: "Roofing Contractor Vienna VA", to: "/roofing-contractor-vienna-va" },
    ],
  },

  mclean: {
    path: "/roofing-contractor-mclean-va",
    serviceType: "Roofing Contractor",
    citySlug: "mclean",
    eyebrow: "Serving McLean & Fairfax County, VA",
    urgency: "Same-Day Service Available · Free Inspections",
    h1: "Fast Roof Repair in McLean, VA",
    metaTitle: "Roofing Contractor McLean VA | Fab Build Pro",
    metaDesc:
      "Top-rated roofing contractor in McLean, VA. Roof repair, replacement & storm damage restoration. Free inspections & estimates. Call 703-766-6599.",
    heroImage: "/img/hero-3.webp",
    heroAlt: "Fab Build Pro roofing project in McLean, Virginia",
    ctaHeading: "Get Your Free McLean Roof Inspection",
    intro: [
      "Fab Build Pro is a trusted, GAF Master Elite roofing contractor serving McLean, VA and the surrounding Fairfax County community. From roof repairs and full replacements to storm damage restoration, our local crews deliver premium craftsmanship and honest, upfront pricing — backed by a 100% satisfaction guarantee.",
      "McLean homeowners choose us for our fast response, free inspections, and expertise with the area's mix of custom and luxury homes. Call 703-766-6599 for a free estimate.",
    ],
    sections: [
      {
        h2: "Roofing Services in McLean",
        bullets: [
          "Roof replacement & new installation",
          "Roof repair & leak repair",
          "Storm, wind & hail damage restoration",
          "Free roof inspections & written estimates",
          "Siding, windows, gutters & remodeling",
        ],
        body: [
          "Whether you own a colonial in Langley, a home near Chesterbrook, or a property in West McLean, we have the right roofing solution for your home and budget.",
        ],
      },
      {
        h2: "Neighborhoods We Serve in McLean",
        body: [
          "We serve all of McLean, including Langley, Chesterbrook, Salona Village, Franklin Park, and West McLean, along with the 22101 and 22102 ZIP codes and nearby Tysons Corner.",
        ],
      },
      {
        h2: "Roof Replacement & Repair in McLean",
        body: [
          "As a GAF Master Elite contractor, we install premium roofing systems with enhanced warranties available to only the top 2% of roofers nationwide. Most residential replacements are completed in 1–2 days, and every repair is diagnosed at the source so it's fixed right the first time.",
        ],
      },
      {
        h2: "Storm Damage Restoration",
        body: [
          "When storms roll through Fairfax County, we provide 24/7 response, free inspections, and full insurance claim assistance so McLean homeowners can restore their roofs with minimal out-of-pocket cost.",
        ],
      },
    ],
    faqs: [
      { q: "Do you provide roofing services in McLean, VA?", a: "Yes. Fab Build Pro serves all of McLean (22101, 22102) and the surrounding Fairfax County area with roof repair, replacement, and storm damage restoration. Call 703-766-6599 for a free estimate." },
      { q: "How much does a new roof cost in McLean?", a: "Most roof replacements in the McLean area range from $8,000 to $25,000+ depending on size, pitch, and materials. We provide free inspections, written estimates, and flexible financing." },
      { q: "Are you licensed and insured to work in McLean?", a: "Yes — we are fully licensed and insured and a GAF Master Elite certified contractor serving Fairfax County and the wider DMV." },
    ],
    related: [
      { label: "Roofing Contractor Vienna VA", to: "/roofing-contractor-vienna-va" },
      { label: "Storm Damage Roof Repair", to: "/storm-damage-roof-repair-virginia" },
      { label: "Roof Replacement", to: "/services/roof-replacement" },
      { label: "All Service Areas", to: "/service-areas" },
    ],
  },

  vienna: {
    path: "/roofing-contractor-vienna-va",
    serviceType: "Roofing Contractor",
    citySlug: "vienna",
    eyebrow: "Serving Vienna & Fairfax County, VA",
    urgency: "Same-Day Service Available · Free Inspections",
    h1: "Fast Roof Repair in Vienna, VA",
    metaTitle: "Roofing Contractor Vienna VA | Fab Build Pro",
    metaDesc:
      "Vienna, VA's trusted roofing contractor. Roof repair, replacement & storm damage restoration. Free inspections & estimates. Call 703-766-6599.",
    heroImage: "/img/hero-1.webp",
    heroAlt: "Fab Build Pro roofing project in Vienna, Virginia",
    ctaHeading: "Get Your Free Vienna Roof Inspection",
    intro: [
      "Headquartered right here in Vienna, VA, Fab Build Pro is the local roofing contractor your neighbors trust. From roof repairs and replacements to storm damage restoration, our GAF Master Elite crews deliver quality workmanship, honest pricing, and a 100% satisfaction guarantee.",
      "Because we're based in Vienna, we know the area and respond fast. Call 703-766-6599 for a free, no-obligation roof inspection and estimate.",
    ],
    sections: [
      {
        h2: "Roofing Services in Vienna",
        bullets: [
          "Roof replacement & new installation",
          "Roof repair & leak repair",
          "Storm, wind & hail damage restoration",
          "Roof inspections & maintenance",
          "Siding, windows, gutters & remodeling",
        ],
        body: [
          "From Vienna Woods to Dunn Loring and Wolftrap, we help homeowners across town protect and improve their homes.",
        ],
      },
      {
        h2: "Neighborhoods We Serve in Vienna",
        body: [
          "We serve all of Vienna, including Vienna Woods, Dunn Loring, Wolftrap, and the surrounding 22180 and 22182 ZIP codes, plus nearby Tysons Corner and Oakton.",
        ],
      },
      {
        h2: "Roof Replacement & Repair in Vienna",
        body: [
          "As a GAF Master Elite contractor based in Vienna, we install premium roofing systems with enhanced manufacturer warranties. Whether you need a single repair or a full replacement, we diagnose the real issue and fix it right — most replacements are done in 1–2 days.",
        ],
      },
      {
        h2: "Local Storm Damage Experts",
        body: [
          "Our team provides 24/7 storm response, free inspections, and complete insurance claim assistance so Vienna homeowners can restore storm-damaged roofs with little out-of-pocket cost.",
        ],
      },
    ],
    faqs: [
      { q: "Do you provide roofing services in Vienna, VA?", a: "Yes — Fab Build Pro is based in Vienna and serves the entire area (22180, 22182) with roof repair, replacement, and storm damage restoration. Call 703-766-6599 for a free estimate." },
      { q: "How much does a new roof cost in Vienna?", a: "Most roof replacements in Vienna range from $8,000 to $25,000+ depending on size, pitch, and materials. We offer free inspections, written estimates, and flexible financing." },
      { q: "Are you a local Vienna roofing company?", a: "Yes. Our office is at 1934 Old Gallows Rd STE 350, Vienna, VA 22182. Being local means faster response times and crews who know the area." },
    ],
    related: [
      { label: "Roofing Contractor McLean VA", to: "/roofing-contractor-mclean-va" },
      { label: "Roof Maintenance", to: "/roof-maintenance" },
      { label: "Storm Damage Roof Repair", to: "/storm-damage-roof-repair-virginia" },
      { label: "All Service Areas", to: "/service-areas" },
    ],
  },

  "roof-maintenance": {
    path: "/roof-maintenance",
    serviceType: "Roof Maintenance",
    eyebrow: "Preventative Roof Care · Free Assessment",
    urgency: "Free Roof Assessment · Flexible Scheduling",
    h1: "Roof Maintenance & Inspections in Northern Virginia",
    metaTitle: "Roof Maintenance Services Northern Virginia | Fab Build Pro",
    metaDesc:
      "Preventative roof maintenance & annual roof inspections in Northern Virginia. Extend your roof's life and protect your warranty. Call 703-766-6599.",
    heroImage: MAINT_IMG,
    heroAlt: "Fab Build Pro technician performing roof maintenance in Northern Virginia",
    ctaHeading: "Book Your Free Roof Assessment",
    intro: [
      "Regular roof maintenance is the smartest, most affordable way to protect your home. A maintained roof lasts significantly longer, prevents costly leaks, and keeps your manufacturer warranty valid. In the DMV — with humid summers, severe storms, and winter freeze-thaw cycles — proactive care makes a real difference.",
      "Fab Build Pro's roof maintenance program includes thorough inspections, minor repairs, and preventative care that catches small issues before they become expensive problems. Call 703-766-6599 to schedule.",
    ],
    sections: [
      {
        h2: "What Roof Maintenance Includes",
        bullets: [
          "Full roof inspection with photo documentation",
          "Shingle, flashing & sealant tune-ups",
          "Debris removal from the roof and valleys",
          "Gutter and downspout checks",
          "Minor repairs to stop leaks before they start",
        ],
        body: [
          "Every maintenance visit ends with a written condition report and clear recommendations — no surprises and no pressure.",
        ],
      },
      {
        h2: "Annual Roof Inspections",
        body: [
          "We recommend a professional roof inspection at least once a year, and again after any major storm. Annual inspections catch small issues early and can extend your roof's usable life by years — often for a fraction of the cost of a major repair.",
        ],
      },
      {
        h2: "Preventative Maintenance Pays Off",
        body: [
          "Preventative roof maintenance costs a small fraction of an emergency repair or premature replacement. It also protects your manufacturer warranty, many of which require documented maintenance to stay valid.",
        ],
      },
      {
        h2: "When to Schedule",
        body: [
          "Spring and fall are ideal for routine maintenance, and it's wise to book an inspection after severe storms. If your roof is more than a few years old or you've never had it inspected, now is a great time to start.",
        ],
      },
    ],
    faqs: [
      { q: "How often should I have my roof inspected?", a: "At least once a year, and again after any major storm. Regular inspections catch small issues early and can add years to your roof's life." },
      { q: "What does roof maintenance include?", a: "A full inspection, minor repairs, resealing, debris and gutter clearing, and a photo report of your roof's condition with recommendations." },
      { q: "Is roof maintenance worth the cost?", a: "Absolutely. Preventative maintenance is a small fraction of the cost of major repairs or premature replacement, and it helps keep manufacturer warranties valid." },
    ],
    related: [
      { label: "Roof Repair", to: "/services/roof-repair" },
      { label: "Roof Replacement", to: "/services/roof-replacement" },
      { label: "Storm Damage Roof Repair", to: "/storm-damage-roof-repair-virginia" },
    ],
  },
};

export const landingPageList = Object.entries(landingPages).map(([key, v]) => ({
  key,
  path: v.path,
  label: v.h1,
}));

export const getLandingPage = (key) => landingPages[key];
export const LANDING_BASE = BASE;
